// [BD feat] Signup Form — bd-signup-form.js
// Dev note (Submit Button 2976:3712): "Klaviyo — API Key: THnc2G, List ID: Y55LeU" → the form posts to
// Klaviyo's client-side Subscriptions API (public key + list id come from the section settings via
// data-bd-klaviyo-key / data-bd-klaviyo-list), never to Shopify's customer form.
//  - email marketing consent is recorded on the profile; the birth date, the WhatsApp number (as typed)
//    and the WhatsApp consent are stored as profile properties, the number additionally as phone_number
//    when it normalises to E.164 (default country code = data-bd-phone-prefix).
//  - a rejected phone number must never lose the signup: on a 4xx the request is retried once without
//    phone_number (the number stays in the properties).
//  - success: the card is replaced by the success block, the section gets data-bd-signup-state="success"
//    and `bd:signup:success` is dispatched (bubbles, detail {email, section}) — the hook for the
//    signup-success section (wave 3). Errors show the section's error text.
(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var REVISION = '2025-07-15';

  function normalisePhone(raw, prefix) {
    var v = (raw || '').replace(/[\s().\-\/]/g, '');
    if (!v) return '';
    if (v.indexOf('00') === 0) v = '+' + v.slice(2);
    if (v.charAt(0) !== '+') {
      if (v.charAt(0) === '0') v = v.slice(1);
      v = (prefix || '') + v;
    }
    return /^\+\d{8,15}$/.test(v) ? v : '';
  }

  // "TT MM JJJJ" / "TT.MM.JJJJ" → "JJJJ-MM-TT" (ISO) only for a REAL calendar date (month lengths, leap
  // years) that lies in the past and not before 1900; otherwise null. The HTML `pattern` only checks
  // the shape (kept for no-JS) — this is the semantic check that blocks 99.99.1990 / 31.13.1990.
  function parseBirthDate(raw) {
    var m = (raw || '').trim().match(/^(\d{1,2})[ ./-](\d{1,2})[ ./-](\d{4})$/);
    if (!m) return null;
    var d = parseInt(m[1], 10);
    var mo = parseInt(m[2], 10);
    var y = parseInt(m[3], 10);
    if (y < 1900 || mo < 1 || mo > 12 || d < 1) return null;
    var date = new Date(Date.UTC(y, mo - 1, d));
    if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null; // rolled over → impossible day
    if (date.getTime() >= Date.now()) return null; // must be in the past
    return m[3] + '-' + ('0' + mo).slice(-2) + '-' + ('0' + d).slice(-2);
  }

  // Typing mask (client 2026-10-13/14, ClickUp "Date of birth field - input handling + mobile keyboard"):
  // only digits are typed — the iOS number pad has no dot key — and the dots of "TT.MM.JJJJ" appear by
  // themselves ("20101989" → "20.10.1989"). A typed dot, slash, dash or space closes the current part
  // ("1." → "01.") or is skipped when the part is already full. Pasted or autofilled dates (also
  // "1990-05-12") land in the same shape. The separator follows the field text set in the theme editor:
  // "TT MM JJJJ" types as "20 10 1989", "TT.MM.JJJJ" (or anything else) as "20.10.1989" (client 2026-10-14).
  var DATE_PARTS = [2, 2, 4];

  function maskBirthDate(raw, typing, sep) {
    sep = sep || '.';
    var iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (iso) raw = iso[3] + iso[2] + iso[1];
    var parts = [];
    var cur = '';
    for (var i = 0; i < raw.length && parts.length < 3; i++) {
      var ch = raw.charAt(i);
      if (ch >= '0' && ch <= '9') {
        cur += ch;
        if (cur.length === DATE_PARTS[parts.length]) { parts.push(cur); cur = ''; }
      } else if (/[ .\/,-]/.test(ch) && cur && parts.length < 2) {
        parts.push(('0' + cur).slice(-2));
        cur = '';
      }
    }
    var out = parts.concat(cur ? [cur] : []).join(sep);
    // Moving on: the dot after a full day or month appears straight away
    if (typing && !cur && parts.length > 0 && parts.length < 3) out += sep;
    return out;
  }

  function digitsBefore(value, pos) {
    return value.slice(0, pos).replace(/\D/g, '').length;
  }

  function bindBirthMask(input) {
    input.setAttribute('maxlength', '10');
    var ph = input.placeholder || '';
    var sep = /\s/.test(ph.trim()) && !/[.\/-]/.test(ph) ? ' ' : '.';
    input.addEventListener('input', function (e) {
      var raw = input.value;
      var pos = input.selectionStart == null ? raw.length : input.selectionStart;
      var atEnd = pos >= raw.length;
      var typing = !e.inputType || e.inputType.indexOf('insert') === 0;
      var digits = digitsBefore(raw, pos);
      var next = maskBirthDate(raw, typing && atEnd, sep);
      if (next === raw) return;
      input.value = next;
      if (atEnd || document.activeElement !== input) return;
      // Keep the caret after the same digit when editing in the middle
      var p = 0;
      for (var seen = 0; p < next.length && seen < digits; p++) if (/\d/.test(next.charAt(p))) seen++;
      input.setSelectionRange(p, p);
    });
  }

  // Native constraint-validation hook: custom validity drives the browser's error UI + aria-invalid.
  // The field is optional (empty passes) and the date is only judged on submit; typing clears the error.
  function bindBirthDate(form) {
    var input = form.querySelector('[data-bd-birth-date]');
    if (!input) return null;
    bindBirthMask(input);
    var message = input.dataset.bdBirthError || input.getAttribute('title') || '';
    function check() {
      var v = input.value.trim();
      // [BD feat] aria-invalid mirrors the custom validity (wave-3 review)
      if (!v) { input.setCustomValidity(''); input.removeAttribute('aria-invalid'); return; } // optional field
      var ok = !!parseBirthDate(v);
      input.setCustomValidity(ok ? '' : message);
      if (ok) input.removeAttribute('aria-invalid');
      else input.setAttribute('aria-invalid', 'true');
    }
    input.addEventListener('input', function () {
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
    });
    return check;
  }

  function buildPayload(form, opts) {
    var data = new FormData(form);
    var name = String(data.get('first_name') || '').trim();
    var parts = name.split(/\s+/);
    var firstName = parts.shift() || '';
    var lastName = parts.join(' ');
    var email = String(data.get('email') || '').trim();
    var rawPhone = String(data.get('phone_number') || '').trim();
    var phone = normalisePhone(rawPhone, form.dataset.bdPhonePrefix);
    var birth = String(data.get('birth_date') || '').trim();

    // [BD feat] QA5 A-01: the home-newsletter behaviour below is scoped to the homepage form
    // (.bd-signup-form--home); the live pre-launch form keeps production's payload exactly.
    var isHome = !!form.closest('.bd-signup-form--home');
    var properties = {};
    if (!isHome) properties['WhatsApp Consent'] = !!rawPhone && !!data.get('consent');
    properties['Signup Source'] = form.dataset.bdKlaviyoSource || '';
    // [/BD feat]
    var attributes = {
      email: email,
      properties: properties,
      subscriptions: {
        email: { marketing: { consent: 'SUBSCRIBED' } }
      }
    };
    // [BD feat] WhatsApp consent is only a fact where the form asks for a number (homepage newsletter review M002:
    // a form without the field must not write "WhatsApp Consent: false" to every profile)
    if (isHome && form.querySelector('[name="phone_number"]')) {
      attributes.properties['WhatsApp Consent'] = !!rawPhone && !!data.get('consent');
    }
    // [/BD feat]
    if (firstName) attributes.first_name = firstName;
    if (lastName) attributes.last_name = lastName;
    // Name, birth date and phone are optional fields (hidden when their setting is empty): only send what was asked
    if (birth) attributes.properties['Birth Date'] = parseBirthDate(birth);
    if (rawPhone) attributes.properties['WhatsApp Number'] = rawPhone;
    if (phone && !opts.withoutPhone) attributes.phone_number = phone;

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: form.dataset.bdKlaviyoSource || 'Signup form',
          profile: { data: { type: 'profile', attributes: attributes } }
        },
        relationships: {
          list: { data: { type: 'list', id: form.dataset.bdKlaviyoList } }
        }
      }
    };
  }

  function post(form, opts) {
    var url = ENDPOINT + '?company_id=' + encodeURIComponent(form.dataset.bdKlaviyoKey);
    return window.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(buildPayload(form, opts))
    });
  }

  function init(section) {
    if (!section || section.dataset.bdSignupBound) return;
    section.dataset.bdSignupBound = '1';

    var form = section.querySelector('[data-bd-klaviyo-form]');
    if (!form) return;
    var key = form.dataset.bdKlaviyoKey;
    var list = form.dataset.bdKlaviyoList;
    if (!key || !list || !window.fetch) return; // no credentials → the native POST fallback stays

    var submit = form.querySelector('[data-bd-signup-submit]');
    var message = section.querySelector('[data-bd-signup-message]');
    var success = section.querySelector('[data-bd-signup-success]');
    var errorText = section.dataset.bdErrorText || '';
    var isHome = section.classList.contains('bd-signup-form--home'); // [BD feat] QA5 A-01: home-only guards
    var checkBirthDate = bindBirthDate(form);

    function setState(state) {
      section.dataset.bdSignupState = state;
      section.classList.toggle('is-success', state === 'success');
      section.classList.toggle('is-loading', state === 'loading');
      if (submit) submit.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    }

    function showError(text) {
      setState('error');
      if (!message) return;
      message.textContent = text || errorText;
      message.hidden = !message.textContent;
    }

    function showSuccess(email) {
      setState('success');
      if (message) message.hidden = true;
      form.hidden = true;
      // [BD feat] a subscribed form never submits again (review R001): button disabled, submit handler guarded
      if (submit && isHome) submit.disabled = true;
      // [/BD feat]
      if (success) {
        success.hidden = false;
        success.focus();
      }
      var detail = { email: email, section: section, form: form };
      section.dispatchEvent(new CustomEvent('bd:signup:success', { bubbles: true, detail: detail }));
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // [BD feat] no second request while one is in flight or after a successful subscription (review R001)
      if (isHome && (section.dataset.bdSignupState === 'loading' || section.dataset.bdSignupState === 'success')) return;
      // [/BD feat]
      if (checkBirthDate) checkBirthDate(); // semantic date check before the native report
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
      if (section.dataset.bdSignupState === 'loading') return;

      setState('loading');
      if (message) message.hidden = true;
      var email = String(new FormData(form).get('email') || '').trim();

      post(form, {})
        .then(function (res) {
          if (res.ok) return res;
          if (res.status >= 400 && res.status < 500) return post(form, { withoutPhone: true });
          throw new Error('klaviyo ' + res.status);
        })
        .then(function (res) {
          if (!res.ok) throw new Error('klaviyo ' + res.status);
          showSuccess(email);
        })
        .catch(function () {
          showError(errorText);
        });
    });
  }

  var section = document.querySelector('.bd-signup-form');
  if (section) init(section);

  // Shopify theme editor: the section re-renders without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target.querySelector('.bd-signup-form'));
  });
})();
// [BD feat] Signup Form
